import React from 'react';
import { graphql } from 'gatsby';
import styled, { css } from 'styled-components';
import {
  breakpointSizes,
  minBreakpointQuery,
  maxBreakpointQuery,
  standardColours,
  greyColours,
  brandColours,
  brandFonts,
  fontWeights,
  fontSize,
  fluidFontSize,
} from '../styles';
import { Slider, Image, Svg, Link, Button } from './ui';
import WishlistToggle from './WishlistToggle';
import LiveAvailability from './LiveAvailability';
import { formatPrice } from '../utils';
import starIcon from '../images/star-icon.svg';
import greenSpaLogo from '../images/green-spa-logo.img.svg';

const StyledSpaCard = styled.article`
  animation: fadeUp 1s forwards;

  @keyframes fadeUp {
    from {
      opacity: 0;
      transform: translateY(5%);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const StyledInner = styled.div`
  height: 100%;

  ${({ isChoice, altTheme }) => {
    if (altTheme) {
      return css`
        color: ${standardColours.white};
        background-color: ${brandColours.primary};
        border: 2px solid ${brandColours.quinary};
      `;
    } else if (isChoice) {
      return css`
        background-color: ${standardColours.white};
        border: 2px solid ${brandColours.primary};
      `;
    } else {
      return css`
        background-color: ${standardColours.white};
        border: 1px solid ${greyColours.lightGrey};
      `;
    }
  }}

  ${({ isRecentlyViewed }) => {
    if (isRecentlyViewed) {
      return css`
        ${minBreakpointQuery.small`
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        `}
      `;
    }
  }}
`;

const StyledTop = styled.div`
  ${minBreakpointQuery.small`
    display: grid;
    grid-template-columns: 1fr 1.6fr;
    gap: 5px;
  `}

  ${minBreakpointQuery.large`
    gap: 15px;
  `}

  ${minBreakpointQuery.xxlarge`
    gap: 25px;
  `}

  ${({ isRecentlyViewed }) => {
    if (isRecentlyViewed) {
      return css`
        ${minBreakpointQuery.small`
          display: block;
        `}
      `;
    }
  }}
`;

const StyledImages = styled.div`
  position: relative;
  overflow: hidden;
`;

const StyledSlider = styled(Slider)`
  height: 100%;

  .slick-list,
  .slick-track,
  .slick-slide,
  .slick-slide > div {
    height: 100%;
  }

  .slick-track {
    margin: 0;
  }

  .slick-dots {
    left: initial;
    right: 10px;
    bottom: 12px;
    transform: initial;
  }
`;

const StyledImage = styled(Image)`
  ${maxBreakpointQuery.small`
    aspect-ratio: 9/5;
  `}

  ${maxBreakpointQuery.tiny`
    aspect-ratio: 2/1;
  `}

  ${minBreakpointQuery.small`
    height: 100%;
  `}

  img {
    opacity: 1 !important;
  }
`;

const StyledWishlistToggle = styled(WishlistToggle)`
  position: absolute;
  top: 10px;
  left: 10px;
`;

const StyledTag = styled.p`
  position: absolute;
  bottom: 0;
  left: 0;
  display: inline-block;
  padding: 8px 14px;
  color: ${({ isDeal }) =>
    isDeal ? brandColours.primary : standardColours.white};
  font-weight: ${fontWeights.bold};
  ${fontSize(14)};
  background-color: ${({ isDeal }) =>
    isDeal ? brandColours.quaternary : brandColours.primary};

  ${({ isRecentlyViewed }) => {
    if (!isRecentlyViewed) {
      return css`
        ${minBreakpointQuery.small`
          bottom: 10px;
        `}

        ${minBreakpointQuery.large`
          bottom: 15px;
        `}
      `;
    }
  }}
`;

const StyledDetails = styled.div`
  display: flex;
  flex-direction: column;
  padding: 15px 20px 20px;
`;

const StyledRating = styled.p`
  align-self: end;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  padding: 6px 10px;
  color: ${brandColours.primary};
  ${fontSize(11)};
  background-color: ${brandColours.duodenary};

  ${minBreakpointQuery.small`
    ${fontSize(12)};
  `}

  ${minBreakpointQuery.large`
    ${fontSize(13)};
  `}

  svg {
    height: 12px;
    width: 12px;
    fill: ${brandColours.primary};
  }
`;

const StyledHeader = styled.header`
  ${({ isSustainable }) => {
    if (isSustainable) {
      return css`
        position: relative;
        padding-right: 50px;
      `;
    }
  }}
`;

const StyledLocation = styled.p`
  margin-bottom: 5px;
  color: ${brandColours.quinary};
  ${fontSize(13)};

  ${minBreakpointQuery.large`
    ${fontSize(15)};
  `}
`;

const StyledName = styled.h3`
  margin: 5px 0;
  ${brandFonts.secondary};
  ${fluidFontSize(
    '30px',
    '35px',
    breakpointSizes.tiny,
    breakpointSizes.xxlarge,
  )};
  line-height: 1.2;
`;

const StyledGreenSpaLogo = styled.img`
  position: absolute;
  top: 50%;
  right: -5px;
  height: 40px;
  width: 40px;
  transform: translateY(-50%);

  ${minBreakpointQuery.small`
    right: -10px;
    height: 45px;
    width: 45px;
  `}

  ${minBreakpointQuery.large`
    right: -15px;
    height: 50px;
    width: 50px;
  `}
`;

const StyledText = styled.p`
  margin-top: 10px;
  color: ${({ altTheme }) =>
    altTheme ? standardColours.white : greyColours.darkPurplishGrey};
  line-height: 1.4;
`;

const StyledPackagesCount = styled.p`
  margin-top: 16px;
  ${brandFonts.secondary};
  ${fontSize(19)};
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
`;

const StyledBottom = styled.div`
  padding: 0 20px 20px;

  ${({ isRecentlyViewed }) => {
    if (isRecentlyViewed) {
      return css`
        ${minBreakpointQuery.small`
          padding-top: 20px;
        `}

        ${minBreakpointQuery.mlarge`
          margin-top: auto;
        `}
      `;
    } else {
      return css`
        ${minBreakpointQuery.small`
          display: flex;
          gap: 20px;
        `}
      `;
    }
  }}
`;

const StyledFeaturedPoints = styled.ul`
  columns: 2 20px;
  list-style-type: disc;

  ${maxBreakpointQuery.small`
    margin-bottom: 20px;
  `}

  ${minBreakpointQuery.small`
    flex-grow: 1;
    margin-top: 20px;
  `}

  ${minBreakpointQuery.large`
    margin-right: 10px;
    margin-left: 10px;
  `}

  ${minBreakpointQuery.xxlarge`
    margin-right: 20px;
    margin-left: 20px;
  `}
`;

const StyledFeaturedPoint = styled.li`
  break-inside: avoid-column;
  margin: 5px 0 5px 18px;
  ${fontSize(14)};
`;

const StyledPriceButton = styled.div`
  align-self: end;
  margin-left: auto;
  text-align: center;
`;

const StyledPrice = styled.p`
  margin-bottom: 12px;
  color: ${({ altTheme }) =>
    altTheme ? brandColours.octonary : greyColours.darkerGrey};
  white-space: nowrap;

  span {
    ${fontSize(24)};

    strong {
      color: ${({ altTheme }) =>
        altTheme ? standardColours.white : brandColours.primary};
      font-weight: ${fontWeights.medium};
    }
  }
`;

const StyledButton = styled(Button)`
  width: 100%;
  white-space: nowrap;
`;

const SpaCard = spa => {
  const {
    name,
    images,
    rating,
    location,
    shortDescription,
    packageCount,
    liveAvailability,
    featuredPoints,
    minPrice,
    higherPrice,
    deal,
    url,
    isChoice,
    isLuxury,
    isSustainable,
    isPackage,
    isPricePerPerson,
    isFirstInSpaList,
    isRecentlyViewed,
    altTheme,
    ...props
  } = spa;

  const sliderOptions = {
    arrows: false,
    dots: true,
    dotsAltColours: true,
  };

  return (
    <StyledSpaCard {...props}>
      <StyledInner
        isChoice={isChoice}
        isRecentlyViewed={isRecentlyViewed}
        altTheme={altTheme}
      >
        <StyledTop isRecentlyViewed={isRecentlyViewed}>
          {images && images.length > 0 && (
            <StyledImages>
              <StyledSlider {...sliderOptions}>
                {images.map(({ url, title }, i) => (
                  <StyledImage
                    key={i}
                    src={url}
                    alt={title}
                    width={575}
                    height={320}
                    layout="constrained"
                    {...(isFirstInSpaList &&
                      i === 0 && {
                        loading: 'eager',
                        fetchpriority: 'high',
                      })}
                  />
                ))}
              </StyledSlider>
              {!isPackage && <StyledWishlistToggle spa={spa} />}
              {(deal || isChoice || isLuxury) && (
                <StyledTag isDeal={deal} isRecentlyViewed={isRecentlyViewed}>
                  {deal
                    ? `${deal.text} ${deal.amount}`
                    : isChoice
                      ? 'SpaSeekers Choice'
                      : 'Luxury Spa'}
                </StyledTag>
              )}
            </StyledImages>
          )}
          <StyledDetails>
            {rating > 0 && (
              <StyledRating>
                <Svg image={starIcon} />
                Customer Rating: {rating}/5
              </StyledRating>
            )}
            <StyledHeader isSustainable={isSustainable}>
              <StyledLocation>
                {typeof location === 'string' ? (
                  location
                ) : location.url ? (
                  <Link to={location.url}>
                    {location.name && `${location.name}, `}
                    {`${location.city}, ${location.county.name}`}
                  </Link>
                ) : (
                  `${location.city}, ${location.county.name}`
                )}
              </StyledLocation>
              <StyledName>
                <Link to={url}>{name}</Link>
              </StyledName>
              {isSustainable && (
                <StyledGreenSpaLogo
                  src={greenSpaLogo}
                  alt="Green Spa"
                  loading="lazy"
                />
              )}
            </StyledHeader>
            {shortDescription && (
              <StyledText
                dangerouslySetInnerHTML={{ __html: shortDescription }}
                altTheme={altTheme}
              />
            )}
            {packageCount && (
              <StyledPackagesCount>
                {packageCount} Packages Available at this Spa
              </StyledPackagesCount>
            )}
            {liveAvailability && <LiveAvailability />}
          </StyledDetails>
        </StyledTop>
        <StyledBottom isRecentlyViewed={isRecentlyViewed}>
          {featuredPoints && featuredPoints.length > 0 && (
            <StyledFeaturedPoints>
              {featuredPoints.map((featuredPoint, id) => (
                <StyledFeaturedPoint key={id}>
                  {featuredPoint}
                </StyledFeaturedPoint>
              ))}
            </StyledFeaturedPoints>
          )}
          <StyledPriceButton>
            {minPrice && (
              <StyledPrice altTheme={altTheme}>
                From{' '}
                <span>
                  {higherPrice && (
                    <>
                      <s>{formatPrice(higherPrice)}</s>{' '}
                    </>
                  )}
                  <strong>{formatPrice(minPrice)}</strong>
                </span>{' '}
                per {isPricePerPerson ? 'person' : 'package'}
              </StyledPrice>
            )}
            <StyledButton
              to={url}
              alt={(!isChoice && true) || altTheme}
              arrow={true}
            >
              {isPackage ? 'View Spa Deal' : 'View Details & Book'}
            </StyledButton>
          </StyledPriceButton>
        </StyledBottom>
      </StyledInner>
    </StyledSpaCard>
  );
};

export default SpaCard;

export const SpaCardFragment = graphql`
  fragment SpaCardFragment on SPASEEKERS_SpaSearchResult {
    spaId
    name
    tags
    images {
      url
      title
    }
    city
    county {
      name
    }
    parentUrl
    location {
      latitude
      longitude
    }
    shortDescription
    packageCount: packages
    facilities
    spaRating
    minPrice
    higherPrice
    isPricePerPerson
    url
    isChoice
    isLuxury
  }
`;

export const SpaPackageCardFragment = graphql`
  fragment SpaPackageCardFragment on SPASEEKERS_PackageSearchResult {
    packageId
    name
    shortDescription
    liveAvailability
    features
    minPrice
    isPricePerPerson
    deal {
      text
      amount
    }
    url
    isLuxury
    spa {
      spaId
      name
      tags
      images {
        url
        title
      }
      city
      county {
        name
      }
      location {
        latitude
        longitude
      }
      spaRating
      url
      isChoice
    }
  }
`;
